import useResizeObserver from '@react-hook/resize-observer';
import { axisBottom, axisLeft, format, scaleBand, scaleLinear, select } from 'd3';
import dayjs from 'dayjs';
import { FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { createSearchParams } from 'react-router-dom';

import { Loader, useDialog } from 'components';
import { SelectedLocationContext } from 'contexts/selected-location';
import useTimeZone from 'hooks/use-timezone';
import fetchWithRetry from 'services/fetch-retry';
import { ChartComponentProps } from '../chart-card';
import DateRangeDialog from './date-range';

const MARGIN_X = 48;
const MARGIN_Y = 32;
const FONT_SIZE = 16;

type TransactionCount = {
  date: string;
  gateInCount: number;
  gateOutCount: number;
  liftOnCount: number;
  liftOffCount: number;
};

const GroupBy: { [P in Extract<dayjs.OpUnitType, 'month' | 'week' | 'day' | 'hour'>]: string } = {
  month: 'MONTHS',
  week: 'WEEKS',
  day: 'DAYS',
  hour: 'HOURS'
};

const DateTransactions: FC<ChartComponentProps> = ({ boxRef, setTitleElement }) => {
  const navigate = useNavigate();
  const { selectedLocation } = useContext(SelectedLocationContext);
  const settingsDialog = useDialog();
  const { dateTimeLocalToUtc, newZonedDayJs } = useTimeZone();

  /* refs */
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  /* state */
  const [{ width, height }, setDimensions] = useState({ height: 0, width: 0 });
  const [counts, setCounts] = useState<TransactionCount[]>([]);
  const [hovered, setHovered] = useState<[MouseEvent, TransactionCount]>();
  const [groupBy, setGroupBy] = useState<keyof typeof GroupBy>('day');
  const [timeRange, setTimeRange] = useState<number>(7);
  const [loading, setLoading] = useState<boolean>(false);

  /* layout effects */
  useLayoutEffect(() => {
    setDimensions(() => ({
      height: (boxRef.current?.clientHeight ?? MARGIN_Y) - MARGIN_Y,
      width: (boxRef.current?.clientWidth ?? MARGIN_X) - MARGIN_X
    }));
  }, [boxRef]);

  useResizeObserver(boxRef.current, ({ contentBoxSize }) => {
    setDimensions({
      height: contentBoxSize[0].blockSize - MARGIN_Y,
      width: contentBoxSize[0].inlineSize - MARGIN_X
    });
  });

  useEffect(
    () =>
      setTitleElement?.(
        <div className="flex-grow-1 d-flex justify-content-end">
          <button className="btn-back p-2" onClick={settingsDialog.openDialog}>
            <span className="material-icons icon-large">settings</span>
          </button>
        </div>
      ),
    [settingsDialog.openDialog, setTitleElement]
  );

  /* data hooks */

  const dateFormat = useMemo(
    () => (groupBy === 'week' || groupBy === 'day' ? 'MMM D' : groupBy === 'hour' ? 'h A' : 'MMM YYYY'),
    [groupBy]
  );
  const startDate = useMemo(
    () =>
      dayjs()
        .startOf(groupBy)
        .subtract(timeRange - 1, groupBy),
    [timeRange, groupBy]
  );

  const getCounts = useCallback(async () => {
    setLoading(true);
    const url = new URL(`/api/transactions/counts`, window.location.origin);
    if (selectedLocation) {
      url.searchParams.append('location', String(selectedLocation.id));
    }
    url.searchParams.append('msDateFrom', startDate.valueOf().toString());
    url.searchParams.append('groupBy', GroupBy[groupBy]);
    setCounts(await fetchWithRetry<TransactionCount[]>(url, { method: 'GET' }, []));
    setLoading(false);
  }, [groupBy, selectedLocation, startDate]);

  const timeout = useRef<NodeJS.Timeout>();
  useEffect(() => {
    setLoading(true);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(getCounts, 500);
  }, [getCounts]);

  const dateKeys = useMemo(() => counts.map(({ date }) => dayjs.utc(date).format(dateFormat)), [counts, dateFormat]);

  const noData: boolean = useMemo(
    () => !counts?.some(data => data.gateInCount || data.gateOutCount || data.liftOnCount || data.liftOffCount),
    [counts]
  );

  /* callbacks */

  const handleClick = useCallback(
    (_: MouseEvent, { date }: TransactionCount) => {
      navigate({
        pathname: '/transactions',
        search: createSearchParams({
          start: encodeURIComponent(dateTimeLocalToUtc(date)),
          end: encodeURIComponent(newZonedDayJs(date).add(1, groupBy).toISOString())
        }).toString()
      });
    },
    [groupBy, dateTimeLocalToUtc, navigate, newZonedDayJs]
  );

  const renderBarPlot = useCallback(() => {
    const svg = select(svgRef.current);
    svg.selectAll('g').remove();

    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'max-width: 100%; height: auto;');

    // Declare the x (horizontal position) scale.
    const x = scaleBand().domain(dateKeys).range([MARGIN_X, width]).padding(0.1);

    const getTotal = (d: TransactionCount): number =>
      Math.max(d.gateInCount + d.gateOutCount, d.liftOnCount + d.liftOffCount);

    // Declare the y (vertical position) scale.
    const y = scaleLinear()
      .domain([0, Math.max(...counts.map(getTotal))])
      .range([height - MARGIN_Y, MARGIN_Y]);

    // Add a rect for each bar.

    svg
      .append('g')
      // .attr('fill', LIFT_OFF_COLOR)
      .attr('class', 'fill-lift-off')
      .selectAll()
      .data(counts)
      .join('rect')
      .attr('x', d => `${(x(dayjs.utc(d.date).format(dateFormat)) ?? 0) + x.bandwidth() / 2}`)
      .attr('y', d => y(d.liftOnCount + d.liftOffCount))
      .attr('height', d => y(0) - y(d.liftOffCount))
      .attr('width', x.bandwidth() / 2)
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick);

    svg
      .append('g')
      // .attr('fill', LIFT_ON_COLOR)
      .attr('class', 'fill-lift-on')
      .selectAll()
      .data(counts)
      .join('rect')
      .attr('x', d => `${(x(dayjs.utc(d.date).format(dateFormat)) ?? 0) + x.bandwidth() / 2}`)
      .attr('y', d => y(d.liftOnCount))
      .attr('height', d => y(0) - y(d.liftOnCount))
      .attr('width', x.bandwidth() / 2)
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick);

    svg
      .append('g')
      // .attr('fill', GATE_OUT_COLOR)
      .attr('class', 'fill-gate-out')
      .selectAll()
      .data(counts)
      .join('rect')
      .attr('x', d => `${x(dayjs.utc(d.date).format(dateFormat))}`)
      .attr('y', d => y(d.gateInCount + d.gateOutCount))
      .attr('height', d => y(0) - y(d.gateOutCount))
      .attr('width', x.bandwidth() / 2)
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick);

    svg
      .append('g')
      // .attr('fill', GATE_IN_COLOR)
      .attr('class', 'fill-gate-in')
      .selectAll()
      .data(counts)
      .join('rect')
      .attr('x', d => `${x(dayjs.utc(d.date).format(dateFormat))}`)
      .attr('y', d => y(d.gateInCount))
      .attr('height', d => y(0) - y(d.gateInCount))
      .attr('width', x.bandwidth() / 2)
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick);

    // Add the x-axis and label.
    svg
      .append('g')
      .attr('transform', `translate(0,${height - MARGIN_Y})`)
      .call(axisBottom(x).tickSizeOuter(0))
      .call(g =>
        /* offset x-axis ticks (hour only) so bars are between ticks (¯\_(ツ)_/¯) */
        g.selectAll('.tick').attr('transform', function () {
          const transform = select(this).attr('transform');
          if (groupBy !== 'hour') return transform;
          const matches = transform.match(/translate\((\d+\.?\d*)/);
          if (!matches) return transform;
          const translateX = Number(matches[1]);
          if (isNaN(translateX)) return transform;
          return `translate(${translateX - x.bandwidth() / 2 - 2})`;
        })
      )
      .call(g => g.selectAll('.tick text').attr('font-size', FONT_SIZE));

    // Add the y-axis and label, and remove the domain line.
    svg
      .append('g')
      .attr('transform', `translate(${MARGIN_X},0)`)
      .call(
        axisLeft(y)
          .ticks(Math.min(Math.floor(height / 50), Math.max(...counts.map(d => d.gateInCount + d.gateOutCount))))
          .tickFormat(format('.0f'))
      )
      .call(g => g.selectAll('.tick text').attr('font-size', FONT_SIZE))
      .call(g => g.select('.domain').remove());
  }, [counts, dateFormat, dateKeys, groupBy, height, width, handleClick]);

  const renderTooltip = useCallback(() => {
    const tooltip = select(tooltipRef.current);
    const size = tooltip.node()?.getBoundingClientRect();
    if (hovered?.[0]?.target instanceof SVGRectElement) {
      const x = Math.max(0, Math.min(width - (size?.width ?? 0), hovered[0].offsetX));
      const y = Math.max(0 + (size?.height ?? 0), Math.min(height, hovered[0].offsetY));
      tooltip
        .style('transform', `translate(${x}px,calc(${y}px - 100%))`)
        .style('opacity', 1)
        .html(
          `${
            groupBy === 'week'
              ? `${dayjs.utc(hovered[1].date).format('MMM D')} - ${dayjs
                  .utc(hovered[1].date)
                  .add(1, 'week')
                  .format('MMM D')}`
              : groupBy === 'hour'
              ? `${dayjs.utc(hovered[1].date).format('ha')} - ${dayjs.utc(hovered[1].date).add(1, 'hour').format('ha')}`
              : dayjs.utc(hovered[1].date).format('MMMM D')
          }<br/>\
          Lift Off: ${hovered[1].liftOffCount}<br/>\
          Lift On: ${hovered[1].liftOnCount}<br/>\
          Gate Out: ${hovered[1].gateOutCount}<br/>\
          Gate In: ${hovered[1].gateInCount}`
        );
    } else {
      tooltip.style('opacity', 0);
    }
  }, [groupBy, hovered, height, width]);

  useEffect(renderBarPlot, [renderBarPlot]);
  useEffect(renderTooltip, [renderTooltip]);

  return (
    <div id="transaction-bar-chart" className="h-100">
      <Loader loading={loading}>
        <div className="tooltip p-3" ref={tooltipRef} />
        <div className="bar-chart-legend d-flex justify-content-between">
          <div className="d-flex g-2">
            <div className="bg-gate-in" />
            <label>GATE IN</label>
          </div>
          <div className="d-flex g-2">
            <div className="bg-gate-out" />
            <label>GATE OUT</label>
          </div>
          <div className="d-flex g-2">
            <div className="bg-lift-on" />
            <label>LIFT ON</label>
          </div>
          <div className="d-flex g-2">
            <div className="bg-lift-off" />
            <label>LIFT OFF</label>
          </div>
        </div>
        <svg ref={svgRef} />
        {noData && <p className="no-transactions">NO DATA</p>}
      </Loader>
      <DateRangeDialog
        dialogProps={settingsDialog}
        groupBy={groupBy}
        timeRange={timeRange}
        setGroupBy={setGroupBy}
        setTimeRange={setTimeRange}
      />
    </div>
  );
};

export default DateTransactions;
